import { z } from "zod";

// ─── Attachment Schema ───────────────────────────────────

const attachmentBase = z.object({
  code: z.string().min(1, "Code is required"),
  description: z.string().default(""),
  export_name: z.string().default(""),
  price: z.string().default(""),
  percentage: z.string().default(""),
  categories: z.array(z.number()).min(1, "At least one category is required"),
  tags: z.array(z.object({ name: z.string(), value: z.string().max(255).default("") })).default([]),
});

export const attachmentSchema = attachmentBase.refine(
  (d) => !(d.price && d.percentage),
  { message: "Only price or percentage allowed, not both", path: ["price"] },
);

export type AttachmentFormValues = z.infer<typeof attachmentBase>;

// ─── Premium Schema ──────────────────────────────────────

const premiumBase = z.object({
  code: z.string().min(1, "Code is required"),
  description: z.string().default(""),
  export_name: z.string().default(""),
  price: z.string().default(""),
  percentage: z.string().default(""),
  categories: z.array(z.number()).min(1, "At least one category is required"),
  tags: z.array(z.object({ name: z.string(), value: z.string().max(255).default("") })).default([]),
  group: z.string().default(""),
  index: z.string().default(""),
  finish_options: z.array(z.number()).default([]),
  wood_species: z.array(z.number()).default([]),
  _premiumType: z.string().default(""),
});

export const premiumSchema = premiumBase
  .refine((d) => !(d.price && d.percentage), {
    message: "Only price or percentage allowed, not both",
    path: ["price"],
  })
  .refine(
    (d) => {
      if (d._premiumType === "door_style" || d._premiumType === "finish_option") {
        return !!d.export_name;
      }
      return true;
    },
    { message: "Export Name is required", path: ["export_name"] },
  )
  .refine(
    (d) => {
      if (d.index && Number(d.index) < 0) return false;
      return true;
    },
    { message: "Index must be 0 or greater", path: ["index"] },
  );

export type PremiumFormValues = z.infer<typeof premiumBase>;

// ─── Construction Method Schema ──────────────────────────

export const constructionMethodSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().default(""),
  export_name: z.string().min(1, "Export Name is required"),
  percentage: z.string().min(1, "Percentage is required"),
  door_upcharge: z.string().min(1, "Door Upcharge is required"),
  cabinet_upcharge: z.string().min(1, "Cabinet Upcharge is required"),
});

export type ConstructionMethodFormValues = z.infer<typeof constructionMethodSchema>;

// ─── Product Schema ──────────────────────────────────────

export const productSchema = z.object({
  manufacturer_code: z.string().min(1, "Manufacturer Code is required"),
  price: z.string().min(1, "Price is required"),
  category: z.number({ error: "Category is required" }).min(1, "Category is required"),
  description: z.string().default(""),
  base_category: z.string().default(""),
  width: z.string().default(""),
  height: z.string().default(""),
  depth: z.string().default(""),
  nb_base_door: z.string().default(""),
  nb_door_pull: z.string().default(""),
  nb_drawer: z.string().default(""),
  nb_drawer_pull: z.string().default(""),
  nb_tall_door: z.string().default(""),
  nb_wall_door: z.string().default(""),
  hinge: z.string().default("None"),
  nb_adjustable_shelf: z.string().default(""),
  export_name_1: z.string().min(1, "Export Name is required"),
  export_name_2: z.string().default(""),
  export_name_3: z.string().default(""),
  export_name_4: z.string().default(""),
  tags: z
    .array(z.object({ name: z.string(), value: z.string().default(""), display: z.string().optional() }))
    .default([]),
});

export type ProductFormValues = z.infer<typeof productSchema>;
